const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function daysInMonth(year: number, month: number) {
    return new Date(year, month + 1, 0).getDate();
}

export function toLocaleTimeString(dt: DateTimeString | Date, locales?: Intl.LocalesArgument, options?: Intl.DateTimeFormatOptions) {
    if (typeof dt === 'string') {
        return new Date(dt).toLocaleTimeString(locales, options);
    }
    return dt.toLocaleTimeString(locales, options);
}

export function getDate(dt: DateTimeString | Date) {
    if (typeof dt === 'string') {
        return new Date(dt).getDate();
    }

    return dt.getDate();
}

export function getTime(dt: DateTimeString | Date) {
    if (typeof dt === 'string') {
        return new Date(dt).getTime();
    }

    return dt.getTime();
}

export function addDays(dt: Date, count: number) {
    return new Date(dt.getTime() + (count * MS_PER_DAY));
}

export function addHours(dt: Date, count: number) {
    const res = new Date(dt.getTime());
    res.setHours(res.getHours() + count);

    return res;
}

export function addSeconds(dt: Date, count: number) {
    return new Date(dt.getTime() + (count * 1000));
}

export function addMonths(dt: Date, count: number) {
    const res = new Date(dt.getTime());
    const origDate = dt.getDate();

    while (count < 0) {
        res.setDate(0);
        count++;
    }
    while (count > 0) {
        const days = daysInMonth(res.getFullYear(), res.getMonth());
        res.setDate(days + 1);
        count--;
    }

    res.setDate(Math.min(origDate, daysInMonth(res.getFullYear(), res.getMonth())));
    return res;
}

export function addYears(dt: Date, count: number) {
    return addDays(dt, count * 365);
}

export function endOfMonth(dt: Date) {
    const res = new Date(dt.getTime());
    res.setDate(1);
    res.setMonth(res.getMonth() + 1);
    res.setDate(0);

    return res;
}

export function startOfMonth(dt: Date) {
    const res = new Date(dt.getTime());
    res.setDate(1);;

    return res;
}

export function startOfWeek(dt: Date) {
    const res = new Date(dt.getTime());
    res.setDate(res.getDate() - res.getDay());

    return res;
}

export function endOfWeek(dt: Date) {
    return addDays(startOfWeek(dt), 6);
}

export function today(): Date {
    return truncateTime(new Date());
}

export function truncateTime(dt: DateTimeString | Date) {
    if (typeof dt === 'string') {
        dt = new Date(dt);
    }

    const res = new Date(dt.getTime());
    res.setHours(0, 0, 0, 0);

    return res;
}

export function dateFromString(str: string|undefined) {
    const [month, day, year] = (str || '').split('/');

    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
}

export function toString(dt: Date) {
    const month = (dt.getMonth() + 1).toString().padStart(2, '0');
    const day = dt.getDate().toString().padStart(2, '0');

    return `${month}/${day}/${dt.getFullYear()}`;
}

export function monthName(date: Date, locale = 'en-US') {
    return new Intl.DateTimeFormat(locale, { month: 'long' }).format(date);
}

export function monthIndexName(monthIndex: number, locale = 'en-US') {
    return new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2000, monthIndex));
}

export function shortMonthName(date: Date, locale = 'en-US') {
    return new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
}

export function isEmptyDateStr(str: string | null | undefined): boolean {
    if (!str) return true;
    if (str === Empty) return true;
    if (str.startsWith('0001-01-01T00:00:00')) return true;

    return false;
}


export function fromJson(str: DateTimeString): Date | undefined {
    if (isEmptyDateStr(str)) return undefined;

    const yyyy = parseInt(str.substring(0, 4));
    const MM = parseInt(str.substring(5, 7)) - 1;
    const dd = parseInt(str.substring(8, 10));
    const hh = parseInt(str.substring(11, 13));
    const mm = parseInt(str.substring(14, 16));
    const ss = parseInt(str.substring(17, 19));

    return new Date(yyyy, MM, dd, hh, mm, ss);
}

export function toJson(dt: Date | undefined): DateTimeString {
    if (!dt) return Empty;

    const yyyy = dt.getFullYear();
    const MM = (dt.getMonth() + 1).toString().padStart(2, '0');
    const dd = dt.getDate().toString().padStart(2, '0');
    const hh = dt.getHours().toString().padStart(2, '0');
    const mm = dt.getMinutes().toString().padStart(2, '0');
    const ss = dt.getSeconds().toString().padStart(2, '0');

    return `${yyyy}-${MM}-${dd}T${hh}:${mm}:${ss}Z` as DateTimeString;
}

export type DateTimeString = `${number}-${number}-${number}T${number}:${number}:${number}Z`;

export const Empty = '0001-01-01T00:00:00Z' satisfies DateTimeString;